/**
 * TanStack Query hooks for tag management.
 *
 * All tags are site-scoped. Each hook requires a siteSlug parameter.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tagsApi, postTagsApi } from '../api';
import type { Tag, ContentData } from '../types';

// ============================================================================
// Query Keys
// ============================================================================

export const tagKeys = {
  all: (siteSlug: string) => ['sites', siteSlug, 'tags'] as const,
  lists: (siteSlug: string) => [...tagKeys.all(siteSlug), 'list'] as const,
  list: (siteSlug: string) => tagKeys.lists(siteSlug),
  details: (siteSlug: string) => [...tagKeys.all(siteSlug), 'detail'] as const,
  detail: (siteSlug: string, id: string) => [...tagKeys.details(siteSlug), id] as const,
  postTags: (siteSlug: string, postId: string) => ['sites', siteSlug, 'post-tags', postId] as const,
};

// ============================================================================
// Tags Hooks
// ============================================================================

/** Fetch all tags */
export function useTags(siteSlug: string) {
  return useQuery({
    queryKey: tagKeys.list(siteSlug),
    queryFn: () => tagsApi.list(siteSlug, { per_page: 100 }),
    enabled: !!siteSlug,
  });
}

/** Create a new tag */
export function useCreateTag(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ContentData<Tag>) => tagsApi.create(siteSlug, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagKeys.all(siteSlug) });
    },
  });
}

/** Delete a tag */
export function useDeleteTag(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tagsApi.delete(siteSlug, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagKeys.all(siteSlug) });
    },
  });
}

// ============================================================================
// Post Tags Hooks
// ============================================================================

/** Fetch tags for a specific post */
export function usePostTags(siteSlug: string, postId: string) {
  return useQuery({
    queryKey: tagKeys.postTags(siteSlug, postId),
    queryFn: () => postTagsApi.getForPost(siteSlug, postId),
    enabled: !!siteSlug && !!postId,
  });
}

/** Set tags for a post */
export function useSetPostTags(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ postId, tagIds }: { postId: string; tagIds: string[] }) =>
      postTagsApi.setForPost(siteSlug, postId, tagIds),
    onSuccess: (_, { postId }) => {
      queryClient.invalidateQueries({ queryKey: tagKeys.postTags(siteSlug, postId) });
    },
  });
}
